import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Tooltip,
  ButtonGroup,
} from '@mui/material';
import { 
  Add, 
  Delete, 
  MoreVert, 
  ExpandMore, 
  ChevronRight,
  Construction,
  Build,
  Settings,
  AttachMoney,
  TrendingUp,
  Search,
  FilterList,
  Keyboard,
  DragIndicator,
  KeyboardArrowDown
} from '@mui/icons-material';
import type { Estimate } from '../../types/estimateDetail';
import DetailedEstimateSheet from './DetailedEstimateSheet';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import AddPositionDialog, { PositionFormState } from './AddPositionDialog';
import AddTesSectionDialog from './AddTesSectionDialog';
import { TesSectionApi } from '../../services/estimateService';
import { compareSectionNr } from '../../utils/sectionUtils';
import { addPosition, updatePosition, updateRms } from '../../services/estimateService';
import { getEstimate } from '../../services/estimateService';

interface DetailedEstimateTabProps {
  estimate: Estimate;
  positions: any[];
  onBack: () => void;
  onRefresh?: () => void;
}

interface EstimatePosition {
  id: number;
  nr: string;
  basis?: string;
  description: string;
  quantity: number;
  unit: string;
  children: EstimateExpense[];
  positionType: 'detailed' | 'simplified';
  unitPrice?: number;
}

interface EstimateExpense {
  id: number;
  type: 'R' | 'M' | 'S' | 'Kp' | 'Kz' | 'Z';
  description: string;
  unit: string;
  norm: number;
  expenses: number;
  rmsPrice: number;
}

export const generateSampleDetailedPositions = (): EstimatePosition[] => {
    const sampleDescriptions = [
      'KNR 2-02 0101 Roboty ziemne – wykopy ręczne',
      'KNNR 4-01 Fundamenty betonowe B20',
      'KNR 5-05 Stropy gęstożebrowe Teriva',
      'KNR 7-03 Murowanie ścian z bloczków silikatowych',
      'KNR 9-01 Tynki wewnętrzne cementowo-wapienne'
    ];
    const sampleUnits = ['m2','m3','m','kg'];
    const arr: EstimatePosition[] = [];

    for (let i = 1; i <= 100; i++) {
      const qty = parseFloat((Math.random()*900+50).toFixed(3));

      // Co trzecia pozycja będzie uproszczona – brak składowych
      const isSimplified = i % 3 === 0;

      if (isSimplified) {
        arr.push({
          id: i,
          nr: i.toString(),
          description: `${sampleDescriptions[(i-1)%sampleDescriptions.length]}\nSzczegół opisu linii 2\nLinia 3 – dodatkowe info`,
          quantity: qty,
          unit: 'm2',
          positionType: 'simplified',
          unitPrice: parseFloat((Math.random()*200+50).toFixed(2)),
          children: []
        });
        continue;
      }

      const children: any[] = [];
      // Robocizna (2 losowe składowe R)
      for(let r=0;r<2;r++){
        const norm = parseFloat((Math.random()*2+0.1).toFixed(4));
        const expenses = parseFloat((norm*qty).toFixed(4));
        const rmsPrice = parseFloat((Math.random()*50+20).toFixed(2));
        children.push({
          id: i*100 + children.length + 1,
          type:'R',
          description:`Brygada ${r+1}`,
          unit:'r-g',
          norm, expenses, rmsPrice
        });
      }
      // Materiały (2 losowe)
      for(let m=0;m<2;m++){
        const norm = parseFloat((Math.random()*3+0.5).toFixed(4));
        const expenses = parseFloat((norm*qty).toFixed(4));
        const rmsPrice = parseFloat((Math.random()*100+50).toFixed(2));
        children.push({
          id: i*100 + children.length + 1,
          type:'M',
          description:`Materiał ${m+1}`,
          unit: sampleUnits[0],
          norm, expenses, rmsPrice
        });
      }
      // Sprzęt (1)
      const normS = parseFloat((Math.random()*1+0.1).toFixed(4));
      const expensesS = parseFloat((normS*qty).toFixed(4));
      const priceS = parseFloat((Math.random()*80+30).toFixed(2));
      children.push({
        id: i*100 + children.length + 1,
        type:'S', description:'Koparka', unit:'m-g', norm:normS, expenses:expensesS, rmsPrice:priceS
      });

      // Oblicz sumy do procentów
      const sumR = children.filter(c=>c.type==='R').reduce((s,c)=>s+c.norm,0);
      const sumM = children.filter(c=>c.type==='M').reduce((s,c)=>s+c.norm,0);
      const sumS = children.filter(c=>c.type==='S').reduce((s,c)=>s+c.norm,0);
      const kpPercent = 15;
      const kzPercent = 5;
      const zPercent = 8;

      children.push({id:i*100+children.length+1,type:'Kp',description:'Koszty pośrednie',unit:'%',norm:kpPercent,expenses:kpPercent,rmsPrice:0});
      children.push({id:i*100+children.length+1,type:'Kz',description:'Koszty zakupu',unit:'%',norm:kzPercent,expenses:kzPercent,rmsPrice:0});
      children.push({id:i*100+children.length+1,type:'Z',description:'Zysk',unit:'%',norm:zPercent,expenses:zPercent,rmsPrice:0});


      arr.push({
        id: i,
        nr: i.toString(),
        description: `${sampleDescriptions[(i-1)%sampleDescriptions.length]}\nLinia 2 – dodatkowy opis\nLinia 3 – uwagi`,
        quantity: qty,
        unit: 'm2',
        positionType: 'detailed',
        children
      });
    }
    return arr;
  };

const DetailedEstimateTab: React.FC<DetailedEstimateTabProps> = ({ estimate, positions, onRefresh, onBack }) => {
  const handleBackToList = onBack;

  // Konwertuj dane z backendu na lokalny format
  const toLocal = (apiPos: any): EstimatePosition => {
    const basisStr = (apiPos.nr || '').toString().toUpperCase();
    const safeNum = (v:any) => {
      const n = typeof v === 'number' ? v : parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    const children = (apiPos.rms||[]).map((r:any)=>({
       id: r.id,
       type: r.rms_type,
       description: r.description,
       unit: r.unit,
       norm: safeNum(r.norm),
       expenses: safeNum(r.quantity),
       rmsPrice: safeNum(r.rms_price)
    }));

    // dodaj brakujące wiersze procentowe Kp/Kz/Z
    const ensurePercent = (type:'Kp'|'Kz'|'Z', percent:any, desc:string) => {
      const pct = typeof percent==='string' ? parseFloat(percent) || 0 : percent;
      if(!children.find((c:any)=>c.type===type)){
        children.push({
          id: -Math.random(), // tymczasowy ujemny id lokalny
          type,
          description: desc,
          unit:'%',
          norm: pct,
          expenses: pct,
          rmsPrice: 0
        });
      }
    };
    ensurePercent('Kp', (estimate as any).markup_kp ?? 0, 'Koszty pośrednie');
    ensurePercent('Kz', (estimate as any).markup_kz ?? 0, 'Koszty zakupu');
    ensurePercent('Z',  (estimate as any).markup_z  ?? 0, 'Zysk');

    // Wylicz cenę jedn. i wartość dla spójności z innymi widokami
    const isDetailed = apiPos.position_type==='S';
    let derivedUnit = 0;
    if (isDetailed) {
      const sumR = children.filter((c:any)=>c.type==='R').reduce((s:number,c:any)=> s + safeNum(c.norm)*safeNum(c.rmsPrice),0);
      const sumM = children.filter((c:any)=>c.type==='M').reduce((s:number,c:any)=> s + safeNum(c.norm)*safeNum(c.rmsPrice),0);
      const sumS = children.filter((c:any)=>c.type==='S').reduce((s:number,c:any)=> s + safeNum(c.norm)*safeNum(c.rmsPrice),0);
      const kp = (estimate as any).markup_kp ?? 0;
      const kz = (estimate as any).markup_kz ?? 0;
      const zz = (estimate as any).imported_markup_z ?? (estimate as any).markup_z ?? 0;
      const kpUnit = (kp/100) * sumR;
      const kzUnit = (kz/100) * sumM;
      const zUnit  = (zz/100) * (sumR+sumM+sumS+kpUnit+kzUnit);
      derivedUnit = sumR + sumM + sumS + kpUnit + kzUnit + zUnit;
    }
    const qty = safeNum(apiPos.quantity);
    const baseUnit = isDetailed ? derivedUnit : safeNum(apiPos.unit_price);
    const derivedValue = baseUnit * qty;

    return {
      id: apiPos.id,
      nr: (apiPos.seq_no !== undefined ? apiPos.seq_no.toString() : '0'),
      basis: basisStr,
      description: apiPos.description,
      quantity: safeNum(apiPos.quantity),
      unit: apiPos.unit,
      positionType: isDetailed ? 'detailed' : 'simplified',
      unitPrice: baseUnit,
      // @ts-ignore
      value: safeNum(apiPos.value) || derivedValue,
      children
    };
  };

  // Lokalny stan pozycji – umożliwia natychmiastowe odświeżenie widoku po zmianach (np. cen RMS)
  const [localPositions, setLocalPositions] = useState<EstimatePosition[]>(() => positions.map(toLocal));

  // Aktualizuj lokalne pozycje gdy props.positions się zmienią (np. po odświeżeniu danych z backendu)
  useEffect(() => {
    setLocalPositions(positions.map(toLocal));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  // Usuń lokalny stan positions:
  // const { state: positions, setState: setPositions, undo, redo, canUndo, canRedo } = useUndoRedo<EstimatePosition[]>(initialPositions);

  // Gdy estimate zmienia się (np. po odświeżeniu), zaktualizuj positions
  useEffect(()=>{
    if((estimate as any).positions){
      setLocalPositions((estimate as any).positions.map(toLocal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(estimate as any).positions]);

  const { setSectionData, data } = useEstimateData();
  const handleTesSaved = (sec: TesSectionApi) => {
    const prev = (data?.tesRows || []) as any[];
    const idx = prev.findIndex((s:any)=>s.id===sec.id);
    const updated = idx>-1 ? prev.map((s:any)=> s.id===sec.id?sec:s) : [...prev,sec];
    const sorted = updated.sort((a:any,b:any)=>compareSectionNr(a.nr,b.nr));
    setSectionData('tesRows', sorted);
  };
  const [addRmsDialog, setAddRmsDialog] = useState(false);
  const [editPositionDialog, setEditPositionDialog] = useState(false);
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [rmsType, setRmsType] = useState<'R' | 'M' | 'S'>('R');

  // Stan dla wyszukiwania i filtrowania
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Stan dla drag & drop
  const [draggedPositionId, setDraggedPositionId] = useState<number | null>(null);

  // Stany dla menu RMS i edycji komórek
  const [rmsMenuAnchorEl, setRmsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRmsExpense, setSelectedRmsExpense] = useState<{ positionId: number; expenseId: number } | null>(null);

  // Stan rozwiniętych pozycji (dla widoku tabeli)
  const [expandedPositions, setExpandedPositions] = useState<Set<number>>(new Set([1, 2]));

  // Edycja komórek RMS
  const [editingCell, setEditingCell] = useState<{
    positionId: number;
    expenseId: number;
    field: 'description' | 'unit' | 'norm' | 'expenses' | 'rmsPrice';
    value: string;
  } | null>(null);

  // Nawigacja klawiaturowa
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0);
  const [selectedExpenseIndex, setSelectedExpenseIndex] = useState(-1);
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState<string>('');

  // Dialogi
  const [addPositionDialog, setAddPositionDialog] = useState(false);
  const [tesDialogOpen, setTesDialogOpen] = useState(false);

  // Oblicz filtrowane pozycje
  const filteredPositions = localPositions.filter(position => {
    const desc = (position.description || '').toLowerCase();
    const nr   = (position.nr ?? '').toString();
    return desc.includes(searchFilter.toLowerCase()) || nr.includes(searchFilter);
  });

  // Funkcje menu RMS
  const handleRmsMenuClick = (event: React.MouseEvent<HTMLElement>, positionId: number, expenseId: number) => {
    setRmsMenuAnchorEl(event.currentTarget);
    setSelectedRmsExpense({ positionId, expenseId });
  };

  const handleRmsMenuClose = () => {
    setRmsMenuAnchorEl(null);
    setSelectedRmsExpense(null);
  };

  const handleEditRms = () => {
    // Placeholder dla edycji RMS
    console.log('Edytuj nakład RMS:', selectedRmsExpense);
    handleRmsMenuClose();
  };

  const handleDeleteRms = () => {
    if (selectedRmsExpense) {
      // setPositions(prev => prev.map(position => 
      //   position.id === selectedRmsExpense.positionId ? {
      //     ...position,
      //     children: position.children.filter(child => child.id !== selectedRmsExpense.expenseId)
      //   } : position
      // ));
      handleRmsMenuClose();
    }
  };

  // Funkcja obsługi kliknięcia w komórkę RMS - natychmiastowa edycja
  const handleCellClick = (
    positionId: number, 
    expenseId: number, 
    field: 'description' | 'unit' | 'norm' | 'rmsPrice',
    currentValue: string | number
  ) => {
    if (field === 'description' || field === 'unit' || field === 'norm' || field === 'rmsPrice' || field==='expenses') {
      setEditingCell({
        positionId,
        expenseId,
        field,
        value: currentValue.toString()
      });
    }
  };

  // Funkcja zapisywania edycji komórki
  const handleCellSave = () => {
    if (editingCell) {
      // setPositions(prev => prev.map(position => 
      //   position.id === editingCell.positionId ? {
      //     ...position,
      //     children: position.children.map(expense => 
      //       expense.id === editingCell.expenseId ? {
      //         ...expense,
      //         [editingCell.field]: editingCell.field === 'norm' || editingCell.field === 'rmsPrice' || editingCell.field==='expenses'
      //           ? parseFloat(editingCell.value) || 0 
      //           : editingCell.value
      //       } : expense
      //     )
      //   } : position
      // ));
      // wysyłka do backendu
      const payload:any={};
      if(editingCell.field==='description') payload.description=editingCell.value;
      if(editingCell.field==='unit') payload.unit=editingCell.value;
      if(editingCell.field==='rmsPrice') payload.rms_price=parseFloat(editingCell.value)||0;
      // opcjonalnie norm / quantity można dodać później
      if(Object.keys(payload).length) updateRms(estimate.id, editingCell.expenseId, payload);

      setEditingCell(null);
    }
  };

  // Funkcje obsługi akcji
  const handleAddPosition = () => {
    setAddPositionDialog(true);
  };

  const handleInsertPosition = async (data: PositionFormState) => {
    try {
      // 1) dodaj pozycję w backendzie
      const created = await addPosition(estimate.id, data);
      const createdPos = (created.position ?? created); // jeśli backend zwraca {position,rms}

      // 2) natychmiast zaktualizuj widok lokalnie
      const local = toLocal(createdPos);
      setLocalPositions(prev => [...prev, local]);

      // 3) opcjonalnie odświeżenie pełnych danych (np. gdy zależy nam na dodatkowych polach)
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Błąd dodawania pozycji:', err);
    }
  };

  const handleEditPosition = () => {
    setEditPositionDialog(true);
  };

  const handleDeletePosition = () => {
    if (selectedPositionId) {
      // setPositions(prev => prev.filter(p => p.id !== selectedPositionId)); // Usuń lokalne ustawianie positions
      setSelectedPositionId(null);
    }
  };

  const handleAddRMS = (type: 'R' | 'M' | 'S', positionId: number) => {
    setRmsType(type);
    setSelectedPositionId(positionId);
    setAddRmsDialog(true);
  };

  // Obsługa klawiatury globalnej
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) return;

      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) {
        return; // pozwól normalnie pisać w polach tekstowych
      }
      if (addPositionDialog) return; // Nie przechwytuj klawiszy, gdy otwarty jest dialog dodawania pozycji
      if (!isKeyboardFocused || editingCell) return;

      // Pobierz aktualnie zaznaczoną pozycję
      const currentPosition = filteredPositions[selectedPositionIndex];
      if (!currentPosition) return;

      const currentExpandedExpenses = expandedPositions.has(currentPosition.id) ? 
        currentPosition.children.sort((a: EstimateExpense, b: EstimateExpense) => {
          const order = ['R', 'M', 'S', 'Kp', 'Kz', 'Z'];
          return order.indexOf(a.type) - order.indexOf(b.type);
        }) : [];

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (selectedExpenseIndex === -1) {
            // Jestem na pozycji głównej
            if (expandedPositions.has(currentPosition.id) && currentExpandedExpenses.length > 0) {
              // Pozycja jest rozwinięta i ma nakłady - przejdź do pierwszego nakładu
              setSelectedExpenseIndex(0);
            } else {
              // Pozycja nie ma nakładów lub jest zwinięta - przejdź do następnej pozycji
              if (selectedPositionIndex < filteredPositions.length - 1) {
                setSelectedPositionIndex(prev => prev + 1);
              }
            }
          } else {
            // Jestem na nakładzie RMS
            if (selectedExpenseIndex < currentExpandedExpenses.length - 1) {
              // Przejdź do następnego nakładu
              setSelectedExpenseIndex(prev => prev + 1);
            } else {
              // To ostatni nakład - przejdź do następnej pozycji głównej
              if (selectedPositionIndex < filteredPositions.length - 1) {
                setSelectedPositionIndex(prev => prev + 1);
                setSelectedExpenseIndex(-1);
              }
            }
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (selectedExpenseIndex === -1) {
            // Jestem na pozycji głównej - przejdź do poprzedniej pozycji
            if (selectedPositionIndex > 0) {
              setSelectedPositionIndex(prev => prev - 1);
            }
          } else {
            // Jestem na nakładzie RMS
            if (selectedExpenseIndex > 0) {
              // Przejdź do poprzedniego nakładu
              setSelectedExpenseIndex(prev => prev - 1);
            } else {
              // To pierwszy nakład - przejdź do pozycji głównej
              setSelectedExpenseIndex(-1);
            }
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          // Rozwiń pozycję jeśli jest zwinięta
          if (!expandedPositions.has(currentPosition.id) && currentPosition.positionType === 'detailed') {
            setExpandedPositions(prev => new Set([...Array.from(prev), currentPosition.id]));
            setSelectedExpenseIndex(0); // Zaznacz pierwszy nakład
          } else if (expandedPositions.has(currentPosition.id) && selectedExpenseIndex === -1) {
            setSelectedExpenseIndex(0); // Zaznacz pierwszy nakład
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (selectedExpenseIndex !== -1) {
            // Zwiń pozycję i wróć do pozycji głównej
            setExpandedPositions(prev => {
              const newSet = new Set(prev);
              newSet.delete(currentPosition.id);
              return newSet;
            });
            setSelectedExpenseIndex(-1);
          } else {
            // Zwiń pozycję
            setExpandedPositions(prev => {
              const newSet = new Set(prev);
              newSet.delete(currentPosition.id);
              return newSet;
            });
          }
          break;

        case ' ':
          e.preventDefault();
          // Rozwiń/zwiń pozycję
          setExpandedPositions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(currentPosition.id)) {
              newSet.delete(currentPosition.id);
              setSelectedExpenseIndex(-1);
            } else {
              newSet.add(currentPosition.id);
            }
            return newSet;
          });
          break;

        case 'Escape':
          e.preventDefault();
          // Anuluj edycję
          setEditingCell(null);
          break;

        case 'p':
        case 'P':
          e.preventDefault();
          handleAddPosition();
          break;

        case 'e':
        case 'E':
          e.preventDefault();
          if (selectedExpenseIndex === -1) {
            // Edytuj pozycję
            setSelectedPositionId(currentPosition.id);
            handleEditPosition();
          }
          break;

        case 'r':
        case 'R':
          e.preventDefault();
          if (selectedExpenseIndex === -1 && currentPosition.positionType === 'detailed') {
            handleAddRMS('R', currentPosition.id);
          }
          break;

        case 'm':
        case 'M':
          e.preventDefault();
          if (selectedExpenseIndex === -1 && currentPosition.positionType === 'detailed') {
            handleAddRMS('M', currentPosition.id);
          }
          break;

        case 's':
        case 'S':
          e.preventDefault();
          if (selectedExpenseIndex === -1 && currentPosition.positionType === 'detailed') {
            handleAddRMS('S', currentPosition.id);
          }
          break;

        case 'd':
        case 'D':
          e.preventDefault();
          setTesDialogOpen(true);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isKeyboardFocused, editingCell, selectedPositionIndex, selectedExpenseIndex, 
      filteredPositions, expandedPositions, addPositionDialog]);

  // Obsługa klawiatury dla edycji komórek
  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (editingCell) {
      if (e.key === 'Enter') {
        handleCellSave();
      } else if (e.key === 'Escape') {
        setEditingCell(null);
      }
    }
  };

  // Funkcja przełączania rozwinięcia pozycji
  const toggleExpanded = (positionId: number) => {
    setExpandedPositions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(positionId)) {
        newSet.delete(positionId);
      } else {
        newSet.add(positionId);
      }
      return newSet;
    });
  };

  // Funkcja do pobierania koloru chip'a dla typu nakładu
  const getChipColor = (type: string) => {
    switch (type) {
      case 'R': return '#2196f3'; // niebieski
      case 'M': return '#ff9800'; // pomarańczowy
      case 'S': return '#4caf50'; // zielony
      case 'Kp': return '#e91e63'; // różowy
      case 'Kz': return '#9c27b0'; // fioletowy
      case 'Z': return '#ff5722'; // pomarańczowo-czerwony
      default: return '#9e9e9e';
    }
  };

  // Funkcja pomocnicza – oblicza cenę jednostkową dla pozycji
  const computeUnitPrice = (position: EstimatePosition): number => {
    if(position.positionType==='simplified') return position.unitPrice || 0;
    const childArr = position.children;
    const sumR = childArr.filter(c=>c.type==='R').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
    const sumM = childArr.filter(c=>c.type==='M').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
    const sumS = childArr.filter(c=>c.type==='S').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
    let kpUnit=0,kzUnit=0,zUnit=0;
    const kpRow=childArr.find(c=>c.type==='Kp'); if(kpRow) kpUnit = kpRow.norm/100 * sumR;
    const kzRow=childArr.find(c=>c.type==='Kz'); if(kzRow) kzUnit = kzRow.norm/100 * sumM;
    const zRow=childArr.find(c=>c.type==='Z'); if(zRow) zUnit = zRow.norm/100 * (sumR+sumM+sumS+kpUnit+kzUnit);
    return sumR+sumM+sumS+kpUnit+kzUnit+zUnit;
  };

  const recalcPosition = (pos: EstimatePosition, newQuantity:number): EstimatePosition => {
    const newChildren = pos.children.map(exp => {
      if(['R','M','S'].includes(exp.type)){
        return { ...exp, expenses: parseFloat((exp.norm * newQuantity).toFixed(3)) };
      }
      return exp;
    });
    return { ...pos, quantity:newQuantity, children:newChildren };
  };

  const handleUpdatePosition = async (payload: {id:number; field:string; value:any}) => {
    // 1) Aktualizacja lokalnego stanu – widok zmieni się natychmiast
    setLocalPositions(prev => prev.map(pos => {
      if (pos.id !== payload.id) return pos;

      if (payload.field === 'children') {
        return { ...pos, children: payload.value } as EstimatePosition;
      }

      if (payload.field === 'quantity') {
        const qty = typeof payload.value === 'number' ? payload.value : parseFloat(payload.value) || 0;
        return recalcPosition(pos, qty);
      }

      if (payload.field === 'unitPrice') {
        return { ...pos, unitPrice: payload.value } as EstimatePosition;
      }

      return { ...pos, [payload.field]: payload.value } as EstimatePosition;
    }));

    // 2) Zapis do backendu jeżeli wymagany
    try {
      if (payload.field === 'children') {
        // Wylicz nową cenę jedn. i wyślij do backendu
        const posAfter = (payload.value as any[]) ? { ...localPositions.find(p=>p.id===payload.id)!, children: payload.value } as EstimatePosition : undefined;
        if(posAfter){
          const newUnit = computeUnitPrice(posAfter);
          const newValue = newUnit * posAfter.quantity;
          await updatePosition(estimate.id, payload.id, { unit_price: newUnit, value: newValue } as any);
        }
        return;
      }

      if (payload.field === 'quantity') {
        const qty = typeof payload.value === 'number' ? payload.value : parseFloat(payload.value) || 0;
        const newUnit = computeUnitPrice({ ...localPositions.find(p=>p.id===payload.id)!, quantity: qty } as EstimatePosition);
        await updatePosition(estimate.id, payload.id, { quantity: qty, unit_price:newUnit, value: newUnit*qty } as any);
      } else if (payload.field === 'unitPrice') {
        const posAfter = { ...localPositions.find(p=>p.id===payload.id)!, unitPrice: payload.value } as EstimatePosition;
        await updatePosition(estimate.id, payload.id, { unit_price: payload.value, value: payload.value*posAfter.quantity } as any);
      } else {
        const posAfter = { ...localPositions.find(p=>p.id===payload.id)!, [payload.field]: payload.value } as EstimatePosition;
        const newUnit = computeUnitPrice(posAfter);
        await updatePosition(estimate.id, payload.id, { [payload.field]: payload.value, unit_price:newUnit, value:newUnit*posAfter.quantity } as any);
      }
    } catch (err) {
      console.error('Błąd zapisu pozycji:', err);
    }

    // 3) Opcjonalne odświeżenie danych z serwera, by zachować spójność
    if (onRefresh) {
      onRefresh();
    }
  };

  useEffect(()=>{ setSectionData('detailedRows', localPositions); }, [localPositions, setSectionData]);

  return (
    <Box sx={{ height: '100%', width: '100%', outline: 'none', display: 'flex', flexDirection: 'column' }} onKeyDown={handleCellKeyDown} tabIndex={0}>
      <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative', overflow: 'visible', flexShrink: 0, pb: 1 }}>
        {/* Wróć do kosztorysów – nad kontenerem, bez przesuwania układu */}
        <Box
          onClick={() => handleBackToList()}
          sx={{
            position: 'absolute',
            top: '-24px',
            left: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            userSelect: 'none',
            zIndex: 10,
          }}
          aria-label="Wróć do kosztorysów"
          role="button"
        >
          <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '16px',
              fontWeight: 500,
              color: '#1F1FB0'
            }}
          >
            Wróć do kosztorysów
          </Typography>
        </Box>
        {/* Wiersz nagłówka zgodny z pozycją nagłówka na stronie projektów */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0, mb: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '20px',
              lineHeight: '24px',
              fontWeight: 400,
              color: '#020242',
              m: 0
            }}
          >
            {`${estimate.estimateNumber || estimate.id} - ${estimate.name || estimate.objectName || estimate.client || ''}`.trim()}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <DetailedEstimateSheet
          positions={localPositions}
          estimateId={estimate.id}
          markups={{ kp: (estimate as any).kp ?? (estimate as any).markup_kp ?? 0, kz: (estimate as any).kz ?? (estimate as any).markup_kz ?? 0, z: (estimate as any).z ?? (estimate as any).markup_z ?? 0 }}
          precision={estimate.settings ? {
            unitPrice: estimate.settings.precisionUnitPrice,
            rmsPrice: estimate.settings.precisionRmsPrice,
            quantity: estimate.settings.precisionQuantity,
            expenses: estimate.settings.precisionExpenses,
            norm: estimate.settings.precisionNorm,
          } : undefined}
          onAddDivision={() => setTesDialogOpen(true)}
          onAddPosition={handleAddPosition}
          onAddLabor={() => console.log('Dodaj robociznę')}
          onAddEquipment={() => console.log('Dodaj sprzęt')}
          onAddMaterial={() => console.log('Dodaj materiał')}
          onUpdatePosition={handleUpdatePosition}
          undo={() => {}}
          redo={() => {}}
          canUndo={false}
          canRedo={false}
          selectedRowId={selectedRowId}
          onSelectRow={setSelectedRowId}
        />
      </Box>

      {/* Dialog dodawania pozycji */}
      <AddPositionDialog
        open={addPositionDialog}
        onClose={() => setAddPositionDialog(false)}
        onInsert={handleInsertPosition}
        lastPositionNr={localPositions.length > 0 ? localPositions[localPositions.length - 1].nr : undefined}
      />

      <AddTesSectionDialog
        open={tesDialogOpen}
        estimateId={estimate.id}
        onClose={() => setTesDialogOpen(false)}
        lastPositionNr={localPositions.length > 0 ? localPositions[localPositions.length - 1].nr : undefined}
        onSaved={handleTesSaved}
      />
    </Box>
  );
};

export default DetailedEstimateTab;
